import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { returnsAPI, bookingsAPI, productsAPI, extractErrorMessage } from '../api';

const RETURN_STATUSES = ['GOOD_CONDITION', 'DAMAGED', 'MISSING'];

const Returns = () => {
  const { selectedStore } = useStore();
  const [returns, setReturns] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    booking_id: '', product_id: '', return_status: 'GOOD_CONDITION',
    deposit_held: 0, damage_cost: 0, notes: '',
  });

  const loadReturns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await returnsAPI.list(selectedStore.id);
      setReturns(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load returns'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadBookings = useCallback(async () => {
    try {
      const response = await bookingsAPI.list(selectedStore.id, { status: 'active' });
      setBookings(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list(selectedStore.id);
      setProducts(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadReturns();
      loadBookings();
      loadProducts();
    }
  }, [selectedStore, loadReturns, loadBookings, loadProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        store_id: selectedStore.id,
        deposit_held: parseFloat(formData.deposit_held) || 0,
        damage_cost: parseFloat(formData.damage_cost) || 0,
      };
      // Auto-calculate
      const depositRefunded = data.return_status === 'GOOD_CONDITION' ? data.deposit_held : Math.max(0, data.deposit_held - data.damage_cost);
      const lossAmount = data.return_status === 'MISSING' ? data.deposit_held : (data.return_status === 'DAMAGED' ? data.damage_cost : 0);
      data.deposit_refunded = depositRefunded;
      data.loss_amount = lossAmount;

      await returnsAPI.create(data);
      loadReturns();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const resetForm = () => {
    setFormData({ booking_id: '', product_id: '', return_status: 'GOOD_CONDITION', deposit_held: 0, damage_cost: 0, notes: '' });
    setShowForm(false);
    setError(null);
  };

  const depositRefunded = formData.return_status === 'GOOD_CONDITION'
    ? parseFloat(formData.deposit_held) || 0
    : Math.max(0, (parseFloat(formData.deposit_held) || 0) - (parseFloat(formData.damage_cost) || 0));
  const lossAmount = formData.return_status === 'MISSING'
    ? parseFloat(formData.deposit_held) || 0
    : (formData.return_status === 'DAMAGED' ? parseFloat(formData.damage_cost) || 0 : 0);

  const getStatusBadge = (status) => {
    const map = { GOOD_CONDITION: 'badge-success', DAMAGED: 'badge-warning', MISSING: 'badge-danger' };
    return map[status] || 'badge-info';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading returns...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Returns</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}>
          {showForm ? 'Cancel' : '+ Process Return'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Process Return</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Booking *</label>
                  <select className="form-select" value={formData.booking_id}
                    onChange={(e) => setFormData({ ...formData, booking_id: parseInt(e.target.value) })} required>
                    <option value="">Select Booking</option>
                    {bookings.map(b => (
                      <option key={b.id} value={b.id}>#{b.booking_number || b.id} - {b.customer_name || b.customer?.name || 'Customer'}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Product *</label>
                  <select className="form-select" value={formData.product_id}
                    onChange={(e) => setFormData({ ...formData, product_id: parseInt(e.target.value) })} required>
                    <option value="">Select Product</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Return Status *</label>
                  <select className="form-select" value={formData.return_status}
                    onChange={(e) => setFormData({ ...formData, return_status: e.target.value })} required>
                    {RETURN_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Deposit Held</label>
                  <input type="number" className="form-input" value={formData.deposit_held} step="0.01" min="0"
                    onChange={(e) => setFormData({ ...formData, deposit_held: e.target.value })} />
                </div>
                {formData.return_status === 'DAMAGED' && (
                  <div className="form-group">
                    <label className="form-label">Damage Cost</label>
                    <input type="number" className="form-input" value={formData.damage_cost} step="0.01" min="0"
                      onChange={(e) => setFormData({ ...formData, damage_cost: e.target.value })} />
                  </div>
                )}
              </div>
              <div className="kpi-grid" style={{ marginBottom: '1rem' }}>
                <div className="kpi-card">
                  <div className="kpi-label">Deposit Refunded</div>
                  <div className="kpi-value" style={{ fontSize: '1.25rem' }}>${depositRefunded.toFixed(2)}</div>
                </div>
                <div className="kpi-card warning">
                  <div className="kpi-label">Loss Amount</div>
                  <div className="kpi-value" style={{ fontSize: '1.25rem' }}>${lossAmount.toFixed(2)}</div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.notes} rows={2}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Process Return</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="mobile-cards hidden-desktop">
        {returns.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">↩️</div>
            <h3 className="empty-state-title">No returns found</h3>
          </div>
        ) : (
          returns.map(ret => (
            <div className="mobile-card" key={ret.id}>
              <div className="mobile-card-header">
                <div className="mobile-card-title">Booking #{ret.booking_number || ret.booking_id}</div>
                <span className={`badge ${getStatusBadge(ret.return_status)}`}>{ret.return_status?.replace(/_/g, ' ')}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Product</span>
                <span>{ret.product_name || `#${ret.product_id}`}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Deposit</span>
                <span>${parseFloat(ret.deposit_held || 0).toFixed(2)}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Refunded</span>
                <span>${parseFloat(ret.deposit_refunded || 0).toFixed(2)}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Date</span>
                <span>{ret.created_at ? new Date(ret.created_at).toLocaleDateString() : '-'}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="card desktop-table">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Booking</th><th>Product</th><th>Status</th><th>Deposit</th><th>Damage Cost</th><th>Refunded</th><th>Loss</th>
              </tr>
            </thead>
            <tbody>
              {returns.map(ret => (
                <tr key={ret.id}>
                  <td>{ret.created_at ? new Date(ret.created_at).toLocaleDateString() : '-'}</td>
                  <td>#{ret.booking_number || ret.booking_id}</td>
                  <td>{ret.product_name || `#${ret.product_id}`}</td>
                  <td><span className={`badge ${getStatusBadge(ret.return_status)}`}>{ret.return_status?.replace(/_/g, ' ')}</span></td>
                  <td>${parseFloat(ret.deposit_held || 0).toFixed(2)}</td>
                  <td>${parseFloat(ret.damage_cost || 0).toFixed(2)}</td>
                  <td>${parseFloat(ret.deposit_refunded || 0).toFixed(2)}</td>
                  <td className="font-bold">${parseFloat(ret.loss_amount || 0).toFixed(2)}</td>
                </tr>
              ))}
              {returns.length === 0 && (
                <tr><td colSpan="8" className="text-center text-muted" style={{ padding: '3rem' }}>No returns found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Returns;
